import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Search, Sparkles, Upload, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import ModuleTile from '../components/ModuleTile';
import { modules as staticModules } from '../data/modules';
import { useAuth } from '../context/AuthContext';
import { SECTIONS } from '../lib/sectionTheme';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const T = SECTIONS.training;

// Render-shape used by ModuleTile. DB modules have the same field names but
// no Lucide icon component; fall back to BookOpen when the icon name isn't
// in the static map.
function dbModuleToTile(dbm) {
  return {
    id: dbm.id,
    title: dbm.title,
    subtitle: dbm.subtitle || '',
    icon: dbm.icon || 'BookOpen',
    color: dbm.color || '#0EA5E9',
    description: dbm.description || '',
    steps: Array.from({ length: dbm.step_count || 0 }, () => ({ title: '', content: '' })),
    _dynamic: true,
  };
}

export default function K4ResourcePage() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'superuser'].includes((user?.role || '').toLowerCase());

  const [progress, setProgress] = useState([]);
  const [dbModules, setDbModules] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { ok, message }
  const fileRef = useRef(null);

  const refetchDbModules = async () => {
    try {
      const r = await axios.get(`${API}/modules`);
      setDbModules(r.data || []);
    } catch (e) {
      // non-fatal — fall back to static-only rendering
    }
  };

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/training/progress`, { withCredentials: true })
      .then(r => setProgress(r.data)).catch(() => {});
    refetchDbModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Merge: prefer the static (richly-formatted) module if its id matches a
  // DB row; render any DB-only modules (PDF imports, future custom uploads)
  // as additional tiles after the static list.
  const merged = useMemo(() => {
    const staticIds = new Set(staticModules.map(m => m.id));
    const dbOnly = (dbModules || []).filter(m => !staticIds.has(m.id)).map(dbModuleToTile);
    return [...staticModules, ...dbOnly];
  }, [dbModules]);

  const moduleProgress = useMemo(() => {
    const byModule = {};
    progress.forEach(p => {
      if (!byModule[p.module_id]) byModule[p.module_id] = new Set();
      byModule[p.module_id].add(p.step_index);
    });
    return merged.map(m => ({
      ...m,
      completed: byModule[m.id]?.size || 0,
      pct: m.steps.length ? Math.round(((byModule[m.id]?.size || 0) / m.steps.length) * 100) : 0,
    }));
  }, [merged, progress]);

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/modules/intake/pdf`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000, // GPT-5.2 + 30-page PDF can take 60-120s
      });
      if (r.data?.inserted) {
        setUploadResult({
          ok: true,
          message: `Created "${r.data.module.title}" with ${r.data.module.steps.length} steps. Module appears below.`,
        });
        await refetchDbModules();
      } else {
        setUploadResult({
          ok: false,
          message: r.data?.error || 'Could not extract a usable module from this PDF. Try a text-based (non-scanned) PDF.',
        });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setUploadResult({ ok: false, message: `Upload failed: ${detail}` });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="k4-resource-page">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8" data-testid="k4-hero">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1.5" style={{ color: T.color }}>Resource library</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Kestral <span style={{ color: T.color }}>K4</span> Resource
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
            Reference material for the Kestral K4 RIS — reporting, billing, worklists, workflows.
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link to="/search" data-testid="k4-search-link"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: T.color }}>
              <Search className="w-4 h-4" /> Search the library
            </Link>
            <div className="inline-flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 border border-border rounded-lg px-3 py-2" data-testid="k4-chat-hint">
              <Sparkles className="w-3.5 h-3.5" style={{ color: T.color }} />
              <span>Use the K4 Resource Assist chatbot (bottom-right) for instant answers.</span>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="mb-6 border border-dashed border-border rounded-xl p-4 bg-muted/20" data-testid="k4-pdf-intake">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Upload className="w-4 h-4" style={{ color: T.color }} />
                  Auto-create module from a Kestral PDF
                </div>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                  Drop a Karisma / Kestral training PDF — GPT-5.2 will extract the structure, generate steps, and add it as a new module. Takes 30–90 seconds. Admin-only.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handlePdfUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid="k4-pdf-intake-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="k4-pdf-intake-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                  style={{ backgroundColor: T.color, color: '#fff' }}
                >
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Extracting…</> : <><Upload className="w-4 h-4" /> Upload PDF</>}
                </button>
              </div>
            </div>
            {uploadResult && (
              <div
                data-testid="k4-pdf-intake-result"
                className={`mt-3 flex items-start gap-2 text-xs px-3 py-2 rounded-lg ${uploadResult.ok ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30' : 'bg-red-500/10 text-red-700 dark:text-red-300 border border-red-500/30'}`}
              >
                {uploadResult.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                <span>{uploadResult.message}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-4 h-4" style={{ color: T.color }} />
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Topics</h2>
          <span className="text-[11px] text-muted-foreground">· {moduleProgress.length} modules</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="modules-grid">
          {moduleProgress.map((mod) => <ModuleTile key={mod.id} module={mod} progress={mod.pct} />)}
        </div>
      </div>
    </div>
  );
}
